import { useEffect, useState, useCallback } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/components/Layout";
import { GameCard } from "@/components/GameCard";
import { HailMaryPanel } from "@/components/HailMaryPanel";
import { previewToolsEnabled } from "@/lib/previewTools";
import { useAuth } from "@/context/AuthContext";

export default function Picks() {
  const { user } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "owner");
  const previewTools = previewToolsEnabled();
  const [hmPreview, setHmPreview] = useState(false);
  const [weeks, setWeeks] = useState([]);
  const [activeWeek, setActiveWeek] = useState(null);
  const [games, setGames] = useState([]);
  const [picks, setPicks] = useState({});
  const [loading, setLoading] = useState(true);
  const [tieValue, setTieValue] = useState("");
  const [sdActive, setSdActive] = useState(false);
  const [sdWeek, setSdWeek] = useState(null);
  const [sdPicks, setSdPicks] = useState({});
  const [sdResults, setSdResults] = useState({});

  const loadShowdown = useCallback(async (week) => {
    try {
      const { data } = await api.get(`/showdown/game-picks?week=${week}`);
      setSdActive(data.active);
      setSdWeek(data.week);
      setSdPicks(data.picks || {});
      setSdResults(data.results || {});
    } catch { setSdActive(false); }
  }, []);

  const loadWeek = useCallback(async (week) => {
    setLoading(true);
    const [g, p] = await Promise.all([
      api.get(`/weeks/${week}/games`),
      api.get(`/picks?week=${week}`),
    ]);
    setGames(g.data);
    const map = {};
    p.data.forEach((pk) => { map[pk.game_id] = pk; });
    setPicks(map);
    const tie = g.data.find((x) => x.is_tiebreaker);
    if (tie && map[tie.id]?.predicted_total != null) setTieValue(String(map[tie.id].predicted_total));
    else setTieValue("");
    setLoading(false);
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/season/current");
      setWeeks(data.weeks);
      const wk = data.current_week || data.weeks[0]?.week;
      setActiveWeek(wk);
      await loadWeek(wk);
      await loadShowdown(wk);
    })();
  }, [loadWeek, loadShowdown]);

  const selectWeek = async (week) => {
    setActiveWeek(week);
    await loadWeek(week);
    await loadShowdown(week);
  };

  const handlePick = async (gameId, team) => {
    const prev = picks[gameId];
    setPicks((s) => ({ ...s, [gameId]: { ...(s[gameId] || {}), game_id: gameId, selected_team: team } }));
    try {
      await api.post("/picks", { game_id: gameId, selected_team: team });
    } catch (err) {
      setPicks((s) => ({ ...s, [gameId]: prev }));
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const tieGame = games.find((g) => g.is_tiebreaker);

  const saveTie = async () => {
    if (!tieGame || tieValue === "") return;
    try {
      await api.post("/picks/tiebreaker", { game_id: tieGame.id, predicted_total: parseInt(tieValue, 10) });
      toast.success("Tiebreaker saved!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const madeCount = Object.values(picks).filter((p) => p.selected_team).length;
  const openCount = games.filter((g) => !g.locked).length;

  const handleShowdownPick = async (gameId, category, team) => {
    const prev = sdPicks[gameId] || {};
    const next = { ...prev, [category]: prev[category] === team ? null : team };
    setSdPicks((s) => ({ ...s, [gameId]: next }));
    try {
      await api.post("/showdown/game-pick", { game_id: gameId, [category]: next[category] || "" });
    } catch (err) {
      setSdPicks((s) => ({ ...s, [gameId]: prev }));
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const showdownOn = sdActive && activeWeek === sdWeek;

  return (
    <PageShell testid="picks-page">
      <div className="mb-6">
        <span className="overline">Weekly Picks · Single-tap to select</span>
        <h1 className="font-display text-4xl font-black tracking-tight mt-2">Make Your Picks</h1>
        <p className="text-zinc-400 text-sm mt-2">
          {madeCount} picks made · {openCount} game{openCount === 1 ? "" : "s"} still open · locked cards can't be changed.
        </p>
        <div className="mt-4" data-testid="hm-preview-section" hidden={!previewTools}>
          {previewTools && (<>
          <button data-testid="hm-preview-toggle" onClick={() => setHmPreview((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-purple-500/40 bg-purple-500/10 text-xs font-semibold text-purple-200 hover:border-purple-400 hover:text-purple-100 transition-colors">
            {hmPreview ? "Hide Hail Mary Preview" : "Preview Hail Mary (test)"}
          </button>
          {hmPreview && (
            <p className="text-xs text-zinc-500 mt-2 max-w-xl">
              Test-only preview of the regular-season Hail Mary section (mock Week 15). Writes NOTHING to the database — no real cards used, no points, regular season stays inactive.
            </p>
          )}
          </>)}
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6" data-testid="week-tabs">
        {weeks.map((w) => (
          <button key={w.week} data-testid={`week-tab-${w.week}`} onClick={() => selectWeek(w.week)}
            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
              activeWeek === w.week ? "border-[#1D4ED8] text-[#4C8DFF] bg-[#1D4ED8]/15" : "border-[#1F1F22] text-zinc-400 hover:text-white"
            }`}>
            {w.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 neon-text animate-spin" /></div>
      ) : games.length === 0 ? (
        <p className="text-zinc-500 py-16 text-center">No games scheduled for this week.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {games.map((g, i) => (
            <GameCard key={g.id} game={g} pick={picks[g.id]} onPick={handlePick} index={i}
              showdownActive={showdownOn} showdownPick={sdPicks[g.id]} onShowdownPick={handleShowdownPick}
              showdownResult={sdResults[g.id]} />
          ))}
        </div>
      )}

      {tieGame && (
        <div className="mt-8 rounded-2xl border border-[#D50A0A]/30 bg-[#D50A0A]/5 p-6" data-testid="tiebreaker-panel">
          <span className="overline">Tiebreaker</span>
          <h2 className="font-display text-xl font-bold mt-2">Predict the combined score</h2>
          <p className="text-zinc-400 text-sm mt-1">
            {tieGame.away} @ {tieGame.home} — total points scored by both teams.
          </p>
          <div className="flex items-center gap-3 mt-4">
            <input data-testid="tiebreaker-input" type="number" value={tieValue} disabled={tieGame.locked}
              onChange={(e) => setTieValue(e.target.value)}
              className="w-32 rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A] disabled:opacity-50" placeholder="e.g. 44" />
            <button data-testid="tiebreaker-save-btn" onClick={saveTie} disabled={tieGame.locked || tieValue === ""}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D50A0A] text-white font-bold hover:shadow-[0_0_15px_rgba(213,10,10,0.4)] active:scale-95 transition-transform disabled:opacity-50">
              <Save className="h-4 w-4" /> Save
            </button>
          </div>
          {tieGame.locked && <p className="text-zinc-500 text-xs mt-2">This game is locked.</p>}
        </div>
      )}

      <HailMaryPanel />
      {previewTools && hmPreview && <HailMaryPanel preview />}
    </PageShell>
  );
}
